import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, Crown, Users, MapPin, BookOpen, Gift, Zap, Heart, Shield, ArrowRight, Play, Target, TrendingUp, Clock, Navigation, Download, BarChart3, NotebookPen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import AuthModal from '../components/AuthModal';
import PaymentModal from '../components/PaymentModal';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';

const MembershipPage = () => {
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleStartTrial = (plan) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    } else {
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    // Handle post-payment success logic
  };

  const plans = [
    {
      name: 'Explorer',
      tier: 'free',
      priceDisplay: 'FREE',
      priceNote: null,
      description: 'Browse our growing library of curated walks',
      badge: null,
      features: [
        'Browse all published walks',
        'View routes, photos & highlights',
        'Save walks to your favorites',
        'Filter by category and tags',
        'Mobile-friendly experience'
      ],
      limitations: [],
      buttonText: 'Start Exploring',
      buttonVariant: 'outline',
      popular: false,
      value: 'Always free',
      highlight: ''
    },
    {
      name: 'Wanderloop+',
      tier: 'wanderloop_plus_annual',
      stripePriceId: 'price_1TLPngIg3fDgQAnj0quMD2z4',
      priceDisplay: '$29',
      priceSuffix: '/year',
      priceNote: 'Just $2.42/month, billed annually',
      description: 'Support the project and unlock what\'s next',
      badge: 'FOUNDING MEMBER PRICING',
      features: [
        'Everything in Explorer',
        'Early access to new walks before anyone else',
        'Curated gear recommendations for every walk',
        'Founding member badge on your profile',
        'Shape the product — vote on features and cities'
      ],
      limitations: [],
      buttonText: 'Become a Founding Member — $29/year',
      buttonVariant: 'default',
      popular: true,
      value: 'Founding member rate — locked in forever',
      highlight: ''
    },
    {
      name: 'Wanderloop+ Monthly',
      tier: 'wanderloop_plus_monthly',
      stripePriceId: 'price_1TLPolIg3fDgQAnjX4iFx2mW',
      priceDisplay: '$9.99',
      priceSuffix: '/month',
      priceNote: 'Billed monthly — $120/year',
      description: 'Support the project and unlock what\'s next',
      badge: null,
      features: [
        'Everything in Explorer',
        'Early access to new walks before anyone else',
        'Curated gear recommendations for every walk',
        'Founding member badge on your profile',
        'Shape the product — vote on features and cities'
      ],
      limitations: [],
      buttonText: 'Get Wanderloop+ Monthly',
      buttonVariant: 'outline',
      popular: false,
      value: 'Cancel anytime',
      highlight: ''
    }
  ];


  const stats = [
    { number: "Los Angeles", label: "Where it starts", icon: <MapPin className="h-5 w-5" /> },
    { number: "Growing Library", label: "And expanding", icon: <BookOpen className="h-5 w-5" /> },
    { number: "New Walks Weekly", label: "Curated by locals", icon: <TrendingUp className="h-5 w-5" /> },
    { number: "Founding Pricing", label: "Lock it in now", icon: <Star className="h-5 w-5" /> }
  ];

  const urgencyFactors = [
    { icon: <Clock className="h-4 w-4" />, text: "Founding pricing — won't last forever" },
    { icon: <TrendingUp className="h-4 w-4" />, text: "New walks added every week" },
    { icon: <Target className="h-4 w-4" />, text: "Help shape what gets built next" }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <SEOHead
        title="Wanderloop+ Membership — Support the Project, Get Early Access"
        description="Become a founding member of Wanderloop. Get early access to new walks, curated gear picks, and help shape the future of urban walking discovery."
        keywords="wanderloop membership, urban walks membership, walking routes subscription, founding member"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-24 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iNCIvPjwvZz48L2c+PC9zdmc+')] animate-pulse"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-2 rounded-full font-bold text-sm mb-6">
            <Crown className="h-4 w-4" />
            FOUNDING MEMBER PRICING
          </div>

          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[1.2]">
            Be Part of Something
            <span className="block pb-2 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
              Being Built
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed font-medium">
            Wanderloop is a growing collection of curated urban walks, starting in Los Angeles.
            Become a founding member and help shape what comes next.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              size="lg"
              onClick={() => handleStartTrial(plans[1])}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-4 text-xl shadow-2xl hover:shadow-yellow-500/25 transform hover:scale-105 transition-all duration-300"
            >
              <Crown className="mr-3 h-6 w-6" />
              Become a Founding Member
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 font-bold px-8 py-4 text-xl"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            >
              <Play className="mr-2 h-5 w-5" />
              See Pricing
            </Button>
          </div>
          
          {/* Urgency indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-blue-200">
            {urgencyFactors.map((factor, index) => (
              <div key={index} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                {factor.icon}
                <span className="font-medium">{factor.text}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-400/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-purple-400/20 rounded-full animate-pulse delay-300"></div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white shadow-lg">
                  {stat.icon}
                </div>
                <div className="text-3xl font-black text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-lg font-bold px-4 py-2 mb-6">
              FOUNDING MEMBER PRICING
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-900">Simple, Honest Pricing</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Wanderloop is free to explore. Become a founding member to get more and help us grow.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card
                key={plan.name}
                className={`relative overflow-hidden transition-all duration-500 ${
                  plan.popular
                    ? 'ring-4 ring-yellow-400 shadow-2xl bg-gradient-to-br from-white to-yellow-50'
                    : 'hover:shadow-xl hover:transform hover:scale-105 bg-white'
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-center py-3 text-sm font-black animate-pulse">
                    {plan.badge}
                  </div>
                )}

                <CardHeader className={`text-center ${plan.badge ? 'pt-16' : 'pt-8'}`}>
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Crown className="h-8 w-8 text-white" />
                  </div>

                  <CardTitle className="text-2xl font-black text-gray-900">{plan.name}</CardTitle>
                  <div className="py-6">
                    <div>
                      <span className="text-5xl font-black text-gray-900">{plan.priceDisplay}</span>
                      {plan.priceSuffix && (
                        <span className="text-gray-600 text-lg">{plan.priceSuffix}</span>
                      )}
                    </div>
                    {plan.priceNote && (
                      <div className="text-sm text-gray-500 font-medium mt-2">
                        {plan.priceNote}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-700 font-medium">{plan.description}</p>
                  {plan.value && (
                    <Badge className="w-full justify-center bg-gradient-to-r from-green-400 to-emerald-500 text-white font-bold mt-2">
                      {plan.value}
                    </Badge>
                  )}
                </CardHeader>

                <CardContent className="space-y-6 px-8 pb-8">
                  <Button
                    className={`w-full py-4 text-sm font-bold transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black shadow-xl hover:shadow-2xl transform hover:scale-105'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                    }`}
                    variant={plan.buttonVariant}
                    size="lg"
                    onClick={plan.name === 'Explorer' ? () => window.location.href = '/walks' : () => handleStartTrial(plan)}
                  >
                    {plan.buttonText}
                  </Button>

                  {plan.highlight && (
                    <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-3 rounded-lg border-l-4 border-blue-400">
                      <p className="text-sm font-bold text-blue-800">{plan.highlight}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 text-lg">What's Included:</h4>
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="h-4 w-4 text-white font-bold" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{feature}</span>
                      </div>
                    ))}

                    {plan.limitations.length > 0 && (
                      <>
                        <Separator className="my-4" />
                        <h4 className="font-bold text-gray-500 text-sm">Limitations:</h4>
                        {plan.limitations.map((limitation, limitIndex) => (
                          <div key={limitIndex} className="flex items-start gap-3 opacity-60">
                            <div className="h-6 w-6 mt-0.5 flex-shrink-0 flex items-center justify-center">
                              <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                            </div>
                            <span className="text-sm text-gray-500">{limitation}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-900">Coming Soon for Founding Members</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The Wanderloop app is in development. Founding members get these features first, free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {[
              {
                icon: <Navigation className="h-10 w-10 text-gray-400" />,
                title: "Live GPS Tracking",
                description: "Walk recording and real-time route tracking, coming with the Wanderloop app."
              },
              {
                icon: <Download className="h-10 w-10 text-gray-400" />,
                title: "Offline Maps",
                description: "Download walks for areas with no signal. Works without internet connection."
              },
              {
                icon: <BarChart3 className="h-10 w-10 text-gray-400" />,
                title: "Advanced Analytics",
                description: "Your walk history, personal stats, and insights dashboard."
              },
              {
                icon: <NotebookPen className="h-10 w-10 text-gray-400" />,
                title: "Walking Journal",
                description: "Log notes, photos, and memories from every walk you complete."
              }
            ].map((feature, index) => (
              <Card key={index} className="relative text-center bg-gray-50 border-dashed border-2 border-gray-200 hover:shadow-md transition-all duration-300">
                <Badge className="absolute top-4 right-4 bg-amber-100 text-amber-700 font-semibold text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  Coming Soon
                </Badge>
                <CardContent className="p-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-black text-xl mb-4 text-gray-700">{feature.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-gray-900">What You Get with Wanderloop</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real features, available today — plus what's coming next for founding members
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <MapPin className="h-10 w-10 text-blue-600" />,
                title: "Curated Walk Library",
                description: "Every walk is hand-picked and personally tested. Detailed routes with photos, waypoints, highlights, and practical tips.",
                value: "Live now"
              },
              {
                icon: <Gift className="h-10 w-10 text-blue-600" />,
                title: "Gear Recommendations",
                description: "Curated gear picks matched to specific walks. Honest recommendations based on what actually works on the ground.",
                value: "Founding members"
              },
              {
                icon: <Star className="h-10 w-10 text-blue-600" />,
                title: "Early Access",
                description: "Be the first to walk new routes before they're published. Founding members get every new walk before anyone else.",
                value: "Founding members"
              },
              {
                icon: <Heart className="h-10 w-10 text-blue-600" />,
                title: "Favorites & Categories",
                description: "Save walks you love. Filter by category, tags, neighborhood, and difficulty to find exactly what you're looking for.",
                value: "Live now"
              },
              {
                icon: <Users className="h-10 w-10 text-blue-600" />,
                title: "Shape the Product",
                description: "Founding members vote on which cities, features, and walk types get built next. Your input directly shapes the roadmap.",
                value: "Founding members"
              },
              {
                icon: <Crown className="h-10 w-10 text-blue-600" />,
                title: "Locked-In Pricing",
                description: "The founding member rate is $29/year — and it stays that way forever, even as we add more cities and features.",
                value: "Limited time"
              }
            ].map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    {feature.icon}
                  </div>
                  <h3 className="font-black text-xl mb-4 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">{feature.description}</p>
                  <Badge className={`font-bold ${
                    feature.value === 'Live now'
                      ? 'bg-amber-100 text-amber-800'
                      : feature.value === 'Founding members'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}>
                    {feature.value}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-6 py-2 rounded-full font-bold text-sm mb-8">
            <Crown className="h-4 w-4" />
            FOUNDING MEMBER PRICING
          </div>

          <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight">
            Be Part of Something Being Built
          </h2>
          <p className="text-xl md:text-2xl mb-12 text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Wanderloop is just getting started. Founding members get the best price, early access,
            and a real voice in what we build next.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Button
              size="lg"
              onClick={() => handleStartTrial(plans[1])}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-6 text-2xl shadow-2xl hover:shadow-yellow-500/25 transform hover:scale-110 transition-all duration-300"
            >
              <Crown className="mr-3 h-8 w-8" />
              Become a Founding Member — $29/year
              <ArrowRight className="ml-3 h-8 w-8" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-blue-100">
            <div className="flex items-center justify-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="font-bold">Cancel anytime</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="font-bold">Price locked in forever</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              <span className="font-bold">Shape the roadmap</span>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Actions */}
      <FloatingActions />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signup"
        prompt="premium"
      />
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        selectedPlan={selectedPlan}
        onSuccess={handlePaymentSuccess}
      />
      
      <Footer />
    </div>
  );
};

export default MembershipPage;